/**
 * Fallback local «Inicio infinito» cuando la API no responde o aún no existe.
 * Alineado con z3312/landing-marketing-secciones-1-6.md
 * y z3312/hero-responsive-banners-backend.md (banner web + móvil).
 */
import {
  faTicket,
  faChartLine,
  faShieldHalved,
  faBell,
  faWallet,
  faDice,
} from "@fortawesome/free-solid-svg-icons";
import heroBannerWeb from "../../../assets/banners/BANNER-GOLD-WEB.png";
import heroBannerMobile from "../../../assets/banners/BANNER-GOLD-MOBILE.png";
import { OFFICIAL_PAYMENT_LOGOS } from "../../../config/paymentLogos";
import { OFFICIAL_SOCIAL_LINKS } from "../../../config/socialLinks";

const asset = (name) => `${process.env.PUBLIC_URL || ""}/landing/${name}`;

/** Banner desktop / landscape por defecto (`/welcome`). */
export const LANDING_HERO_BANNER_DEFAULT = heroBannerWeb;
/** Banner móvil / portrait — breakpoint ≤768px. */
export const LANDING_HERO_BANNER_MOBILE = heroBannerMobile;

/** Alias histórico */
export const LANDING_HERO_BANNER_WEB = LANDING_HERO_BANNER_DEFAULT;

export const LANDING_FALLBACK = {
  is_mock: true,
  page_type: "default",
  slug: null,
  is_active: true,
  hero: {
    background_image_url: LANDING_HERO_BANNER_DEFAULT,
    background_image_mobile_url: LANDING_HERO_BANNER_MOBILE,
    title: "Una Experiencia Exclusiva",
    subtitle: "Para jugar las Loterías de Colombia",
    cta_text: "Comienza a jugar",
    cta_link: "/auth",
  },
  features_section: {
    title: "¿Qué puedes hacer en Gold?",
    subtitle: "",
    items: [
      {
        sort_order: 1,
        title: "Juega en minutos",
        description:
          "Elige tu lotería nacional preferida y personaliza tus números al instante.",
        icon_key: "ticket",
        icon: faTicket,
        image_url: asset("feature-play.jpg"),
        image_alt: "Jugar lotería en minutos",
      },
      {
        sort_order: 2,
        title: "Resultados en tiempo real",
        description:
          "Olvídate de buscar en páginas desactualizadas. Consolida y consulta todos los resultados del país en un solo lugar.",
        icon_key: "chart",
        icon: faChartLine,
        image_url: asset("feature-results.jpg"),
        image_alt: "Resultados en tiempo real",
      },
      {
        sort_order: 3,
        title: "Respaldo y Confianza",
        description:
          "Somos un distribuidor de lotería 100% autorizado. Tus tiquetes digitales cuentan con validez legal.",
        icon_key: "shield",
        icon: faShieldHalved,
        image_url: asset("feature-trust.jpg"),
        image_alt: "Respaldo y confianza",
      },
      {
        sort_order: 4,
        title: "Alertas de Premios",
        description:
          "Te notificamos de inmediato tras cada sorteo si tu billete resulta ganador del premio mayor o de cualquier seco.",
        icon_key: "bell",
        icon: faBell,
        image_url: asset("feature-alerts.jpg"),
        image_alt: "Alertas de premios",
      },
      {
        sort_order: 5,
        title: "Retiros sin complicaciones",
        description:
          "Cobra tus premios menores directamente desde la app. Para premios mayores, te guiamos paso a paso con la entidad oficial correspondiente.",
        icon_key: "wallet",
        icon: faWallet,
        image_url: asset("feature-withdraw.jpg"),
        image_alt: "Retiros sin complicaciones",
      },
      {
        sort_order: 6,
        title: "Generador Aleatorio",
        description:
          "¿Falta de inspiración? Usa nuestra función ¡Dame suerte! y genera combinaciones al azar en un clic.",
        icon_key: "dice",
        icon: faDice,
        image_url: asset("feature-random.jpg"),
        image_alt: "Generador aleatorio",
      },
    ],
  },
  how_to_play_section: {
    title: "¿Cómo jugar en Gold Loterías?",
    subtitle: "",
    steps: [
      {
        step_number: 1,
        title: "Crea tu cuenta y recarga",
        description:
          "Regístrate de forma segura y añade saldo con tus métodos de pago favoritos.",
      },
      {
        step_number: 2,
        title: "Reclama tu bienvenida",
        description:
          "Canjea tu cupón promocional para activar tu bono de bienvenida en tu primera compra.",
      },
      {
        step_number: 3,
        title: "Elige y compra",
        description:
          "Selecciona tu sorteo, digita tus números o usa el generador aleatorio, ¡y listo! Tu billete digital queda guardado en tu historial.",
      },
    ],
  },
  conversion_section: {
    form_title: "Únete a la experiencia Gold",
    form_subtitle: "Para jugar las Loterías de Colombia",
    cta_label: "Registrarme y Jugar",
    contact_title: "Contáctanos",
    mobile_column_title: "Lleva la suerte en tu bolsillo",
    qr_image_url: asset("qr-download.png"),
    play_store_url: "",
    app_store_url: "https://apps.apple.com/co/app/gold-loterias/id6817048934",
    login_link_text: "¿Ya tienes una cuenta? Inicia sesión aquí",
  },
  legal_lotteries_section: {
    title: "Juega legal en las Loterías de Colombia",
    subtitle: "",
    items: [],
  },
  footer_section: {
    company_description:
      "Gold Loterías es una plataforma tecnológica de intermediación operada por Goldia S A S (NIT 901.010.821-1). Facilita la compra de tus billetes de lotería oficial de forma 100% digital, con altos estándares de seguridad para que tu ticket quede emitido y vinculado legalmente a tu identidad.",
    columns: [
      {
        title: "Enlaces rápidos",
        links: [
          { label: "¿Quiénes somos?", url: "" },
          { label: "Preguntas frecuentes", url: "/ayuda" },
          { label: "PQR's", url: "/ayuda" },
          { label: "Manual de Juego Responsable", url: "" },
          { label: "Blog", url: "/blog" },
          { label: "Jugar ahora", url: "/apuestas" },
        ],
      },
      {
        title: "Legal y privacidad",
        links: [
          { label: "Política de privacidad", url: "/politica-de-privacidad" },
          { label: "Términos y condiciones", url: "/terminos-y-condiciones" },
          {
            label: "Política de tratamiento de datos",
            url: "/docs/POLITICA TRATAMIENTO DE DATOS.pdf",
          },
          { label: "Política de cookies", url: "" },
        ],
      },
    ],
    payment_logos: OFFICIAL_PAYMENT_LOGOS,
    regulatory_logos: [],
    social_links: OFFICIAL_SOCIAL_LINKS,
  },
  campaign_overlay: null,
  section_overrides: null,
};

/** @deprecated Use LANDING_FALLBACK — kept for gradual migration */
export const serveSection = {
  title: LANDING_FALLBACK.features_section.title,
  subtitle: LANDING_FALLBACK.features_section.subtitle,
  features: LANDING_FALLBACK.features_section.items,
};

export const howItWorksSection = {
  title: LANDING_FALLBACK.how_to_play_section.title,
  subtitle: LANDING_FALLBACK.how_to_play_section.subtitle,
  steps: LANDING_FALLBACK.how_to_play_section.steps.map((s) => ({
    n: s.step_number,
    title: s.title,
    desc: s.description,
  })),
};

export const downloadSection = {
  titleLine1: "Lleva la suerte",
  titleLine2: "en tu bolsillo",
  subtitle: LANDING_FALLBACK.conversion_section.form_subtitle,
};
